import { SelectorChain, SelectorPart } from "@fullstory/proto/fs/selectors";
import { tagName } from "@fullstory/shared/domutils";
import { WATCH_RULE_COMMENT } from "@fullstory/shared/regexes";
import { nil } from "@fullstory/typeutils";

// This is more aggressive in escaping than the spec [1] requires, for simplicity
// [1] https://drafts.csswg.org/cssom/#common-serializing-idioms
export function cssEscapeIdent(s: string): string {
  // Make sure you keep this in sync with serialize.go, selector_parser.ts and selector.rs
  // TODO: this won't handle unicode characters outside the BMP
  const replacer = (s: string) => {
    if (`[]:.,/<>=?|\\!;+(){}~@#%^&*'$"\` `.indexOf(s) != -1) {
      return "\\" + s;
    }
    return "\\" + s.charCodeAt(0).toString(16) + " ";
  };

  if (s === "-") {
    return "\\-";
  }

  const escapedCharacters = /(^[0-9])|([^0-9a-zA-Z-_])/g;
  if (s[0] === "-") {
    return "-" + s.slice(1).replace(escapedCharacters, replacer);
  }
  return s.replace(escapedCharacters, replacer);
}

// This matches the spec [1] reasonably well, though we don't actually replace NUL with U+FFFD.
// [1] https://drafts.csswg.org/cssom/#common-serializing-idioms
export function cssEscapeString(s: string): string {
  const replacer = (s: string) => {
    if (s === '"' || s === "\\") {
      return "\\" + s;
    }
    return "\\" + s.charCodeAt(0).toString(16) + " ";
  };

  return '"' + s.replace(/[\x01-\x1f\"\\]/g, replacer) + '"';
}

export function serializeSelector(chain: SelectorChain, sorted?: boolean): string {
  let str: string = "";
  for (let rule: SelectorChain | nil = chain; rule; rule = rule.parent) {
    if (!str) {
      str = serializeSelectorChain(rule, sorted);
    } else {
      str = serializeSelectorChain(rule, sorted) + str;
    }
  }
  return str;
}

export const processSelectorChain = (chain: SelectorChain, proc: (selectorPart: SelectorPart) => SelectorPart) => {
  if (chain.rule) {
    chain.rule = proc(chain.rule);
  }

  if (chain.parent) {
    processSelectorChain(chain.parent, proc);
  }
};

function serializeSelectorChain(sel: SelectorChain, sorted?: boolean): string {
  const rule = sel.rule;
  if (!rule) {
    return sel.combinator;
  }
  return sel.combinator + serializeSelectorPart(sel.rule, sorted);
}

export function serializeSelectorPart(part: SelectorPart | null, sorted?: boolean): string {
  let str = "";
  if (!part) {
    return str;
  }
  if (part.tag) {
    str += part.tag === "*" ? part.tag : cssEscapeIdent(part.tag);
  }
  if (part.id) {
    str += "#" + cssEscapeIdent(part.id);
  }
  const classes = [...part.classes];
  classes.sort(); // sort for display purposes
  for (const clazz of classes) {
    str += "." + cssEscapeIdent(clazz);
  }
  const keys = Object.keys(part.attrs);
  if (sorted) {
    keys.sort();
  }
  keys.forEach((attr) => {
    if (part.attrMatch[attr] === "") {
      str += "[" + cssEscapeIdent(attr) + "]";
    } else {
      str += "[" + cssEscapeIdent(attr) + part.attrMatch[attr] + cssEscapeString(part.attrs[attr] ?? '') + "]";
    }
  });
  return str;
}

export function newSelectorPart(): SelectorPart {
  return {
    tag: "",
    id: "",
    classes: [],
    attrs: {},
    attrMatch: {},
    pseudoClasses: [],
  };
}

export function newSelectorChain(from: SelectorChain | null, comb: string, part?: SelectorPart): SelectorChain {
  const ret: SelectorChain = { parent: from, combinator: comb, rule: null };
  if (part) {
    ret.rule = part;
  } else {
    ret.rule = newSelectorPart();
  }
  return ret;
}

export function computeSelectorDepth(chain: SelectorChain): number {
  let depth = 0;
  let current: SelectorChain | null = chain;

  while (current) {
    depth++;
    current = current.parent;
  }

  return depth;
}

// This implements a *very* simplistic parser for the CSS grammar (ie: [1]). To avoid infinite loops, each of the
// rules must either advance idx or throw an exception.
//
// Calling code may pass limits to the selectors that they support as a map of string/boolean. Attribute matches,
// child combinators and the universal ('*') tag are all tested against this map.
//
// [1] https://www.w3.org/TR/2011/REC-css3-selectors-20110929/#w3cselgrammar)
export function parseSelector(str: string, limits: { [key: string]: boolean } | nil = null): SelectorChain {
  let rule: SelectorPart = newSelectorPart();
  let chain: SelectorChain = newSelectorChain(null, "", rule);

  let idx = 0;

  // valid is set to true once we have something that's a valid selector component (ie: a tag, an ID, a class)
  let valid = false;

  const invalid = () => {
    // debugger; // <-- this is a useful place to stop for debugging
    throw "Invalid selector";
  };

  const escape = () => {
    let ch = str[++idx];

    // In theory the spec allows a string to contain a backslash-escaped newline in a string. We don't allow that.
    if (ch === "\n" || ch == null) {
      throw invalid();
    }

    if (ch.match(/[0-9a-fA-F]/)) {
      // hex
      let hex = "";
      do {
        hex += ch;
        ch = str[++idx];
      } while (ch && ch.match(/[0-9a-fA-F]/));

      // Absorb one (and only one) trailing whitespace character after the escape
      if (ch && ch.match(/[\s]/)) {
        idx++;
      }
      return String.fromCharCode(parseInt(hex, 16));
    }

    idx++;
    return ch;
  };

  const ident = (isAttr?: boolean) => {
    const start = idx;
    let i = "";
    let ch;

    // NOTE: The spec doesn't like CSS identifiers to start with numbers, but we are going to relax this, since the
    // server-side code isn't going to be this strict.

    // https://drafts.csswg.org/css-syntax-3/#typedef-ident-token
    while ((ch = str[idx]) != null) {
      // escape
      if (ch === "\\") {
        i += escape();
        continue;
      }

      const validChars = /[a-zA-Z0-9_\-]/;
      if (ch.match(validChars) || ch.charCodeAt(0) > 0x80) {
        i += ch;
        idx++;
        continue;
      }
      break;
    }

    // Can't be empty
    if (start === idx) {
      throw invalid();
    }

    return i;
  };

  const tag = () => {
    // If tag appears, it needs to be the first part
    if (valid) {
      throw invalid();
    }
    valid = true;
    rule.tag = ident().toLowerCase();
  };

  const universal = () => {
    // If a universal selector appears, it needs to be the first part
    if (valid) {
      throw invalid();
    }
    valid = true;
    rule.tag = "*"; // wildcard
    if (limits && !limits["*"]) {
      throw invalid();
    }
    idx++;
  };

  const id = () => {
    valid = true;
    idx++;
    rule.id = ident();
  };

  const clazz = () => {
    valid = true;
    idx++;
    rule.classes.push(ident());
  };

  const ws = () => {
    while (str[idx] != null && str[idx]?.match(/\s/)) {
      idx++;
    }
  };

  const equals = () => {
    ws();
    const ch = str[idx];
    let eq = "=";

    // Various attribute matches
    if (ch === "*" || ch === "|" || ch === "^" || ch === "$" || ch === "~") {
      eq = ch + "=";
      idx++;
    }

    if (limits && !limits[eq]) {
      throw invalid();
    }

    if (str[idx] !== "=") {
      throw invalid();
    }
    idx++;
    ws();
    return eq;
  };

  // Attributes can take the form: [<ws>token<ws>(op)=<ws>token<ws>]
  // Where op can be: *, $, |, ~, ^
  const attr = () => {
    valid = true;
    idx++;
    ws();
    const name = ident(true).toLowerCase();
    let eq = "";
    try {
      eq = equals();
    } catch (e) {
      // Might be selector without a value, like "[attrname]"
      if (limits && !limits["[]"] || e != "Invalid selector") {
        throw e;
      }
      if (str[idx] !== "]") {
        throw invalid();
      }
      idx++;
      rule.attrs[name] = "";
      rule.attrMatch[name] = "";
      return;
    }
    let ch = str[idx];

    let delim;
    if (ch === '"' || ch === "'") {
      delim = ch;
      idx++;
    } else {
      // "legacy" unquoted attribute values - technically these are CSS idents, but those won't deal w/embedded spaces
      delim = "]";
    }

    let value = "";
    while ((ch = str[idx]) !== delim) {
      // fell off the end...
      if (ch == null) {
        throw invalid();
      }

      // escape
      if (ch === "\\") {
        value += escape();
        continue;
      }

      // Don't allow string quotes inside naked attributes
      if (delim === "]") {
        if (ch === '"' || ch === "'") {
          throw invalid();
        }
      }

      // Unconditionally add any character that doesn't match the above rules - hooray for legacy parsing
      value += ch;
      idx++;
    }

    if (delim !== "]") {
      idx++;
      ws();
    }

    if (str[idx] !== "]") {
      throw invalid();
    }

    idx++;
    rule.attrs[name] = value;
    rule.attrMatch[name] = eq;
  };

  const child = (allowEnd: boolean = false) => {
    if (!valid) {
      throw invalid();
    }
    valid = false;
    ws();
    let ch = str[idx];
    if (ch === ">" || ch === "+" || ch === "~") {
      idx++;
      ws();
    } else if (!ch && allowEnd) {
      // nothing but whitespace at the end, this has been deemed allowed
      // we were valid coming into this method, mark valid again and return success
      valid = true;
      return;
    } else {
      ch = " ";
    }
    rule = newSelectorPart();
    chain = newSelectorChain(chain, ch, rule);
    if (limits && !limits[ch]) {
      throw invalid();
    }
  };

  // Users have invented a comment syntax in block rules, so let's not parse these things just in case they end up
  // blocking something
  if (str.indexOf("//") == 0) {
    throw invalid();
  }

  // Consume any leading white space.
  ws();

  let loops = 0;
  for (; idx < str.length;) {
    // Since we never, ever want to hang someone's page...
    if (loops++ > str.length * 2) {
      console.log("Likely caught in an infinite loop while parsing " + str);
      throw new Error("Internal error, infinite loop");
    }
    const ch = str[idx];
    switch (ch) {
      case "#":
        id();
        break;
      case "*":
        universal();
        break;
      case ".":
        clazz();
        break;
      case "[":
        attr();
        break;
      case " ":
        child(true);
        break;
      case ">":
      case "+":
      case "~":
        child();
        break;
      // No pseudos
      case ":":
        throw invalid();
      default:
        tag();
        break;
    }
  }

  // We didn't find a valid simple selector at the end
  if (!valid) {
    throw invalid();
  }

  return chain;
}

function extractClassMap(elem: HTMLElement | nil): { [data: string]: boolean } {
  const classMap: { [cls: string]: boolean } = {};
  if (!elem) {
    return classMap;
  }

  if (elem.classList) {
    // Normal case where browser does whitespace normalization for us.
    for (const className of elem.classList) {
      classMap[className] = true;
    }
  } else {
    // Fallback for IE 9
    // Bloody SVG decided that className should be an SVGAnimatedString.
    // Oh, and it also leaves className === undefined for a more or less random subset of <svg>'s children,
    // including <style>, <filter>, <feGaussianBlur>, et al. Proabably others too, just for fun.
    if (elem.className) {
      const className = elem.getAttribute("class") || "";
      const classes = className.split(" ");
      for (const c of classes) {
        classMap[c.trim()] = true;
      }
    }
  }

  return classMap;
}

// A single component of a SelectorRule.
export class SelectorRuleElem {

  private static attrPredicates: { [key: string]: (attrValue: string, selectorValue: string) => boolean } = {
    "": (a, s) => true,
    "=": (a, s) => a === s,
    "*=": (a, s) => a.indexOf(s) > -1,
    "^=": (a, s) => a.lastIndexOf(s, 0) === 0,
    "$=": (a, s) => a.indexOf(s, a.length - s.length) + s.length === a.length,
    "~=": (a, s) => a.split(/\s+/).indexOf(s) > -1,
    "|=": (a, s) => a.split("-").indexOf(s) > -1,
  };
  constructor(private _parent: SelectorRuleElem | null, private sel: SelectorChain) {
  }

  public parent(): SelectorRuleElem | null {
    return this._parent;
  }

  public matches(elem: HTMLElement | nil, classMap: { [data: string]: boolean }): boolean {
    if (!elem) {
      return false;
    }

    if (this.sel.rule?.tag !== tagName(elem)) {
      return false;
    }

    if (this.sel.rule.id !== elem.id) {
      return false;
    }

    for (const cls of this.sel.rule.classes) {
      if (!(cls in classMap)) {
        return false;
      }
    }

    for (const name in this.sel.rule.attrs) {
      // Note: getAttribute is case-insensitive
      const elemValue = elem.getAttribute(name);
      if (elemValue == null) { return false; }
      const selectValue = this.sel.rule.attrs[name];
      const match = this.sel.rule.attrMatch[name];
      if (match && selectValue && !SelectorRuleElem.attrPredicates[match]?.(elemValue, selectValue)) {
        return false;
      }
    }

    if (this._parent) {
      return this._parent.matches(elem.parentElement, extractClassMap(elem.parentElement));
    }

    return true;
  }
}

// SelectorRule wraps a CSS selector that can be used at recording time or cook time to enforce some "Recording Configuration" that references DOM elements.
// This includes (but is not limited to) exclusion, mask/unmask rules and element watches for generating Has Seen events.
export class SelectorRule {

  // format parses a selector and checks that it is valid (parses properly, and is accepted by the browser's native selector matcher).
  // Returns the serialized version of it to ensure consistent formatting.
  public static format(sel: string): string | null {
    try {
      // Ignore comment-like ('//') lines, since some users use them.
      if (SelectorRule.isComment(sel)) {
        return sel;
      }

      // Throw if the selector isn't supported by our JS selector parser.
      let parsed = new SelectorRule(sel);
      let serialized = parsed.serialize();

      // Throw if the selector isn't supported by the native
      // implementation we're using. This is just a sanity check, since
      // recording will happen on a variety of browsers with different
      // capabilities; we wouldn't want to use this as our only check.
      const elem = document.createElement("div");
      elem.matches(serialized);
      return serialized;
    } catch (e) {
      // Failed one of the checks above.
      console.warn('Failed to format selector: ', e);
      return null;
    }
  }

  public static formatForNot(sel: string): string | null {
    try {
      let parsed = new SelectorRule(sel, {
        "=": true, "[]": true,
        "*=": true, "^=": true,
        "$=": true, "~=": true,
        "|=": true,
      });

      if (parsed.selector.rule) {
        let partsUsed = 0;
        const rule = parsed.selector.rule;
        partsUsed +=
          Object.keys(rule.attrs).length +
          (rule.classes?.length ?? 0) +
          (rule.id ? 1 : 0) +
          (rule.tag ? 1 : 0);

        // :not can only accept one of these (attr/class/id/tag) and not multiple
        if (partsUsed !== 1) {
          return null;
        }

        // :not does not allow nested pseudo-classes.
        if (rule.pseudoClasses.length > 0) {
          return null;
        }
      }
      let serialized = parsed.serialize();

      // sanity check with native implementation
      const elem = document.createElement("div");
      elem.matches(`:not(${serialized})`);
      return serialized;
    } catch (e) {
      return null;
    }
  }

  // isValid returns whether or not the supplied Selector string conforms to our parser and also to the browser's native selector matcher.
  public static isValid(sel: string): boolean {
    return SelectorRule.format(sel) !== null;
  }

  public static isComment(sel: string): boolean {
    return !!sel.match(WATCH_RULE_COMMENT);
  }

  public rules: SelectorRuleElem;
  public selector: SelectorChain;

  constructor(selector: string, limits?: { [combinator: string]: boolean }) {
    this.selector = parseSelector(selector,
      limits ?? {
        ">": true, "=": true,
        "[]": true, "*=": true,
        "^=": true, "$=": true,
        "~=": true, "|=": true,
        " ": true
      });

    const process = (rule: SelectorChain): SelectorRuleElem => new SelectorRuleElem(rule.parent ? process(rule.parent) : null, rule);
    this.rules = process(this.selector);
  }

  public serialize(): string {
    return serializeSelector(this.selector);
  }
}
